/* eslint-disable react/prop-types */
import {
  Box,
  Button,
  Collapse,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import { lighten, darken } from "@mui/system";
import { useTranslation } from "react-i18next";

const FileUploadField = ({
  label,
  fileNames = [],
  onChange,
  onDelete,
  inputId = "file-upload",
  multiple,
  accept = "image/*,application/pdf",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const headerLabel =
    multiple || fileNames.length > 1
      ? t("fileUploadField.selectedFiles")
      : t("fileUploadField.selectedFile");

  return (
    <>
      <input
        type="file"
        multiple={multiple}
        onChange={onChange}
        accept={accept}
        style={{ display: "none" }}
        id={inputId}
      />

      <label htmlFor={inputId}>
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          fullWidth
          sx={{
            color: theme.palette.text,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: "#fff",
            },
          }}
        >
          {label || t("fileUploadField.uploadButton")}
        </Button>
      </label>

      <Collapse in={fileNames.length > 0}>
        <Box
          sx={{
            marginTop: 2,
            padding: 2,
            backgroundColor: isDarkMode
              ? darken(theme.palette.background.paper, 0.2)
              : lighten(theme.palette.background.paper, 0.05),
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            transition: "background-color 0.3s ease",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {headerLabel}
          </Typography>

          <List dense disablePadding>
            {fileNames.map((name, index) => {
              const isPdf = name.toLowerCase().endsWith(".pdf");
              return (
                <ListItem
                  key={index}
                  disableGutters
                  sx={{
                    py: 0.5,
                    px: 1,
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? darken(theme.palette.background.paper, 0.4)
                        : lighten(theme.palette.background.paper, 0.1),
                      borderRadius: 1,
                    },
                  }}
                  secondaryAction={
                    onDelete && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => onDelete(index)}
                        size="small"
                        sx={{
                          color: theme.palette.error.main,
                          "&:hover": {
                            backgroundColor: isDarkMode
                              ? darken(theme.palette.error.main, 0.7)
                              : lighten(theme.palette.error.main, 0.9),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: isPdf
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                        color: "#fff",
                        width: 32,
                        height: 32,
                      }}
                    >
                      {isPdf ? (
                        <PictureAsPdfIcon fontSize="small" />
                      ) : (
                        <ImageIcon fontSize="small" />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    sx={{ wordBreak: "break-word" }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </>
  );
};

export default FileUploadField;
